(() => {
  const root = document.getElementById('shopify-section-page-yotpo-reward');
  if (!root) return;

  const tierIndexes = { Rookie: 0, Pro: 1, MVP: 2, Legacy: 3 };
  const focusableSelector = 'a, button, input, select, textarea, [tabindex]';
  let enhancementFrame;

  const enhanceCampaigns = () => {
    root.querySelectorAll('.yotpo-widget-campaign-widget-icon-container img').forEach(image => {
      image.alt = '';
    });

    root.querySelectorAll('.yotpo-widget-campaign-widget-container div[role="listitem"]').forEach(item => {
      item.removeAttribute('aria-label');
      item.setAttribute('role', 'button');
      item.tabIndex = 0;

      if (item.dataset.lskdKeyboardButton) return;
      item.dataset.lskdKeyboardButton = 'true';
      item.addEventListener('keydown', event => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        item.click();
      });
    });

    root.querySelector('.yotpo-widget-campaign-widget-container div[role="list"]')?.removeAttribute('role');
  };

  const enhanceRedemptions = () => {
    const carousel = root.querySelector('.yotpo-redemption-options-carousel');
    if (!carousel) return;

    carousel.setAttribute('role', 'region');
    carousel.setAttribute('aria-label', 'Products carousel');
    carousel.querySelector('.swiper-container')?.removeAttribute('role');

    if (!carousel.querySelector('[data-lskd-carousel-instructions]')) {
      const instructions = document.createElement('p');
      instructions.className = 'tw-sr-only';
      instructions.dataset.lskdCarouselInstructions = 'true';
      instructions.textContent = 'Use the next and previous buttons to browse products.';
      carousel.prepend(instructions);
    }

    carousel.querySelectorAll('.yotpo-redemption-option-details-text div[title]').forEach(title => {
      title.setAttribute('role', 'heading');
      title.setAttribute('aria-level', '3');
    });

    const wrapper = carousel.querySelector('.swiper-wrapper');
    if (!wrapper) return;

    if (!wrapper.lskdSlideObserver) {
      wrapper.lskdSlideObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          const isVisible = entry.intersectionRatio >= 0.6;
          entry.target.setAttribute('aria-hidden', String(!isVisible));
          entry.target.inert = !isVisible;

          entry.target.querySelectorAll(focusableSelector).forEach(element => {
            if (!isVisible) {
              if (element.dataset.lskdTabindexSaved === undefined) {
                element.dataset.lskdTabindexSaved = 'true';
                if (element.hasAttribute('tabindex')) element.dataset.lskdTabindex = element.getAttribute('tabindex');
              }
              element.tabIndex = -1;
            } else if (element.dataset.lskdTabindexSaved !== undefined) {
              if (element.dataset.lskdTabindex !== undefined) {
                element.setAttribute('tabindex', element.dataset.lskdTabindex);
              } else {
                element.removeAttribute('tabindex');
              }
              delete element.dataset.lskdTabindex;
              delete element.dataset.lskdTabindexSaved;
            }
          });
        });
      }, { root: carousel, threshold: [0, 0.6] });
    }

    const slides = Array.from(wrapper.children);
    slides.forEach((slide, index) => {
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-label', `Slide ${index + 1} of ${slides.length}`);

      if (!slide.dataset.lskdObserved) {
        slide.dataset.lskdObserved = 'true';
        wrapper.lskdSlideObserver.observe(slide);
      }
    });
  };

  const enhanceWidgets = () => {
    enhanceCampaigns();
    enhanceRedemptions();
  };

  const scheduleEnhancements = () => {
    cancelAnimationFrame(enhancementFrame);
    enhancementFrame = requestAnimationFrame(enhanceWidgets);
  };

  const applyTierContent = () => {
    const tiers = window.swellAPI?.getVipTiers?.();
    if (!Array.isArray(tiers)) return;

    [...tiers].sort((a, b) => a.rank - b.rank).forEach((tier, index) => {
      const card = root.querySelector(`.lskd-reward__tier-${index}`);
      if (!card) return;

      card.querySelector('.tier-name')?.replaceChildren(tier.name);
      card.querySelector('h3')?.replaceChildren(tier.name);
      card.querySelector('.perks-card-footer .btn-pill span')?.replaceChildren(tier.description);
    });
  };

  const bannerLink = root.querySelector('.lskd-reward__banner-link');
  const bannerOptIn = root.querySelector('.lskd-reward__banner-optin');
  const bannerOptInButton = root.querySelector('.lskd-reward__banner-optin-button');
  const bannerOptInError = root.querySelector('.lskd-reward__banner-optin-error');
  let optedInLocally = false;

  const applyCustomerState = () => {
    const details = window.swellAPI?.getCustomerDetails?.();
    const customerTier = details?.vipTier?.name;
    const customerTierIndex = tierIndexes[customerTier];

    if (bannerOptIn && details) {
      const optedIn = optedInLocally || details.isOptIn === true;
      bannerOptIn.hidden = optedIn;
      bannerLink.hidden = optedIn;
    }

    Object.values(tierIndexes).forEach(tierIndex => {
      const card = root.querySelector(`.lskd-reward__tier-${tierIndex}`);
      if (!card) return;

      const isCurrent = tierIndex === customerTierIndex;
      const isUnlocked = tierIndex <= 2 || (Number.isInteger(customerTierIndex) && customerTierIndex >= tierIndex);
      card.classList.toggle('active', isCurrent);
      card.querySelector('.perks-card-content')?.setAttribute('aria-hidden', String(!isUnlocked));
      card.querySelector('.emoji')?.setAttribute('aria-hidden', 'true');
    });

    root.querySelector('.lskd-reward__exclusives')?.classList.toggle(
      'active',
      Number.isInteger(customerTierIndex) && customerTierIndex > 1
    );

    scheduleEnhancements();
  };

  bannerOptInButton?.addEventListener('click', () => {
    bannerOptInButton.disabled = true;
    bannerOptInError.hidden = true;

    window.swellAPI.updateOptInCustomer(
      { shouldOptIn: true, rewardOnOptIn: true },
      () => {
        optedInLocally = true;
        applyCustomerState();
        window.swellAPI.refreshCustomerDetails(applyCustomerState);
      },
      () => {
        bannerOptInError.hidden = false;
        bannerOptInButton.disabled = false;
      }
    );
  });

  const observeWidgets = new MutationObserver(scheduleEnhancements);
  observeWidgets.observe(root, { childList: true, subtree: true });

  if (window.jQuery) {
    window.jQuery(document).on('swell:initialized', applyTierContent);
    window.jQuery(document).on('swell:setup', applyCustomerState);
  } else {
    document.addEventListener('swell:initialized', applyTierContent);
    document.addEventListener('swell:setup', applyCustomerState);
  }

  applyTierContent();
  applyCustomerState();
  scheduleEnhancements();
})();
